// =============================================================================
// client/src/lib/flux/connect.js | v1.0 | S79 | P9a — W-7(h) | STUB
// =============================================================================
// The Gmail connect STATE MACHINE as data. The <ConnectGmail> component that
// drives it — mint link, poll /oauth/status at 5 s, verify exactly once, stop —
// is W-11 and is NOT built here. This file is what W-11 fills in against, and
// what P9b's RecruitEdit card consumes unchanged.
//
// ELEVEN STATES, ruled at P9a §0.6 (adjudication B-22). Three documents had
// proposed three different sets: v1's eight, the Cursor overlay's twelve, and
// Codex's seven. The test applied: a state earns its name only if it changes
// at least one of (a) what is rendered, (b) whether Launch/Test may enable,
// (c) which action button appears. States sharing all three are one state.
//
// 🔴 `verified` IS THE ONLY STATE THAT ENABLES LAUNCH OR TEST.
// `connected` and `verified` are DIFFERENT FACTS: /oauth/status proves a token
// ROW exists; /gmail-verify proves the token WORKS. v1 called both "connected"
// and gated Launch on a separate boolean — two sources for one truth, which is
// how a client ships a Launch button over a dead token.
//
// `sending` is P9b's and is deliberately absent here.
// =============================================================================

export const CONNECT_STATES = Object.freeze({
  IDLE: 'idle',                   // no recruit selected
  CHECKING: 'checking',           // "Checking Gmail…"
  NOT_CONNECTED: 'not_connected', // offers Connect / Copy link
  POLLING: 'polling',             // "Waiting for authorization…" + elapsed
  EXPIRED: 'expired',             // past the link's own expiry; offers Recheck
  CONNECTED: 'connected',         // token row exists, Google not asked yet
  VERIFIED: 'verified',           // 🔴 the only state that enables Launch/Test
  REVOKED: 'revoked',             // re-authorizing FIXES it
  BLOCKED: 'blocked',             // a human looks at it; NO action button
  AMBIGUOUS: 'ambiguous',         // >1 connection; admin-owned; render NO mailbox
  ERROR: 'error',                 // transient; offers Recheck
});

// Which states may offer Connect Gmail / Copy link / (P9b) Send Auth Email.
// Never `blocked` (a re-auth cannot fix it) and never `ambiguous` (an admin
// resolves it) — offering a button that cannot work mails a confused kid for
// nothing.
export const STATES_OFFERING_CONNECT = Object.freeze([
  CONNECT_STATES.NOT_CONNECTED,
  CONNECT_STATES.POLLING,
  CONNECT_STATES.EXPIRED,
  CONNECT_STATES.REVOKED,
  CONNECT_STATES.ERROR,
]);

export function offersConnectControls(state) {
  return STATES_OFFERING_CONNECT.includes(state);
}

// 🔴 WHICH STATES HIDE THE SENDING-FROM ADDRESS FIELD. Jake's connected-state
// ruling — "hide the address input once connected" — stated precisely.
//
// THIS IS A SECOND PREDICATE ON PURPOSE. S85 shipped the field's visibility
// keyed on offersConnectControls, and that is ONE predicate answering TWO
// questions: should the CONNECT BUTTONS show, and should the ADDRESS FIELD
// show. They have different correct answers and `checking` is exactly where
// they diverge — a transient state that offers no buttons but must never take
// the field away. Keying the field on the buttons' list unmounted the input
// mid-edit and SILENTLY ATE EVERY KEYSTROKE after a two-second pause, on the
// primary path, in production. Measured in a browser at S85; two independent
// code-reads had both called the mechanism right and the symptom wrong.
//
// ⚠ STATES_OFFERING_CONNECT IS NOT WIDENED TO FIX THIS. The buttons' contract
// is ruled at §0.6 and P9a W-11 and does not move for a layout problem.
//
// ⚠ NAMED FOR WHAT IT CONTROLS, NOT FOR A CLAIM ABOUT THE DATABASE. A token row
// also exists at `revoked`, so a name like "hasTokenOnFile" would be false there
// — and this file has already produced two comments that asserted the opposite
// of the code beneath them. `revoked` is DELIBERATELY EXCLUDED: the token is
// dead, the only remedy is re-authorization, and re-authorizing needs the
// address field. OutreachTab.jsx — the shipped P9b card — renders the input at
// `revoked` too (OC's S85 call, re-openable by Jake).
export const STATES_HIDING_MAILBOX_INPUT = Object.freeze([
  CONNECT_STATES.CONNECTED,
  CONNECT_STATES.VERIFIED,
]);

export function hidesMailboxInput(state) {
  return STATES_HIDING_MAILBOX_INPUT.includes(state);
}

// 🔴 The single gate on real mail leaving a kid's mailbox.
export function enablesLaunch(state) {
  return state === CONNECT_STATES.VERIFIED;
}

// ─── /gmail-verify reason -> state (P9a §0.6, reasons live in ────────────────
// server/src/routes/flux-campaigns.js's gmail-verify handler) ────────────────
//
// send_scope_missing is REVOKED, not blocked: the kid connected but did not
// grant send, and re-authorizing fixes exactly that. Putting it with the
// blocked reasons would hide the one button that works.
//
// identity_divergence and google_config_missing are BLOCKED: one is a wrong
// mailbox pairing, one is a server misconfiguration, and neither is fixed by
// the kid re-authorizing.
//
// timeout is ERROR, not blocked: it is transient. Recheck.
export const VERIFY_REASON_STATES = Object.freeze({
  refresh_failed: CONNECT_STATES.REVOKED,
  token_rejected: CONNECT_STATES.REVOKED,
  api_error: CONNECT_STATES.REVOKED,
  test_exception: CONNECT_STATES.REVOKED,
  refresh_exception: CONNECT_STATES.REVOKED,
  send_scope_missing: CONNECT_STATES.REVOKED,
  identity_divergence: CONNECT_STATES.BLOCKED,
  google_config_missing: CONNECT_STATES.BLOCKED,
  timeout: CONNECT_STATES.ERROR,
});

// Anything unnamed — network failure, an unparseable body, a reason string this
// build has never seen — is ERROR with a Recheck, never a guess at a state that
// would enable or disable Launch on the strength of an unknown.
export function stateForVerifyResult(result) {
  if (result?.authorized === true) return CONNECT_STATES.VERIFIED;
  const mapped = VERIFY_REASON_STATES[result?.reason];
  if (mapped) return mapped;
  if (result?.revoked === true) return CONNECT_STATES.REVOKED;
  return CONNECT_STATES.ERROR;
}

// ─── /oauth/status -> state (P4 W-4) ─────────────────────────────────────────
// ⚠ `ambiguous` renders a COUNT and admin-resolution instructions, never the
// mailbox identities: /oauth/status returns no list, so a card that names them
// is inventing them (Codex, adopted).
export function stateForStatusResult(status) {
  if (status?.ambiguous === true) return CONNECT_STATES.AMBIGUOUS;
  if (status?.connected === true) return CONNECT_STATES.CONNECTED;
  return CONNECT_STATES.NOT_CONNECTED;
}

// Ruling 16's contract, kept as data so W-11 cannot quietly re-tune it:
// poll cheap, and call Google EXACTLY ONCE.
//
// 🔴 S98 — TWO CADENCES, AND THE DEADLINE IS NOT A CONSTANT ANY MORE.
// Jake: "the card should not stop polling after 10 minutes. It should have a
// frequent poll for 10 minutes and then a less frequent (like every 5 mins)
// for the remainder of the 24h."
//
// 🔴 DO NOT REPLACE THIS WITH A TIMEOUT CONSTANT, EVEN NOW THAT BOTH BUTTONS
// AGREE. Both routes mint at 24 hours and both pass ttlMs explicitly (S98,
// Jake: "Why does the copy link die after 15 minutes? It should be 24h").
// ⚠ THEY DID NOT ALWAYS AGREE, AND THAT IS WHY THIS IS STILL SERVER-DRIVEN:
// /oauth/google/url used to pass no ttlMs and inherit signState's 15-minute
// default, so a flat 24 h here would have polled for a day against a link that
// died after fifteen minutes. The lifetimes are one server edit from diverging
// again; reading `expires_at` is correct for whatever they become.
//
// 🟢 SO THE DEADLINE COMES FROM THE SERVER. Both routes return `expires_at` in
// their response body and both components already store it; the client has been
// ignoring it in favour of a hardcoded ten minutes this whole time.
// ⚠ IF YOU CAME HERE LOOKING FOR A TIMEOUT CONSTANT: there isn't one. There is
// a FALLBACK, below, used only when the server's date is missing or unparseable.
export const POLL_INTERVAL_FAST_MS = 5000;              // first 10 min: every 5 s
export const POLL_FAST_PHASE_MS = 10 * 60 * 1000;       // how long the fast phase runs
export const POLL_INTERVAL_SLOW_MS = 5 * 60 * 1000;     // after that: every 5 min
// ⚠ NOT the deadline. Only used when `expires_at` cannot be parsed — better a
// short honest stop than a poll that never ends.
export const POLL_FALLBACK_TIMEOUT_MS = 10 * 60 * 1000;

// The cadence, as a function of how long this episode has been running.
export function pollDelayMs(elapsedMs) {
  return elapsedMs < POLL_FAST_PHASE_MS ? POLL_INTERVAL_FAST_MS : POLL_INTERVAL_SLOW_MS;
}

// The wall-clock instant to stop at. `expiresAt` is the server's own ISO string
// from the mint response — 24 h for an emailed invite, ~15 min for a copied
// link — so one implementation stops both paths at their own real expiry.
// ⚠ Falls back to POLL_FALLBACK_TIMEOUT_MS past `startedAtMs` when the value is
// absent or unparseable, rather than polling forever.
export function pollDeadlineMs(expiresAt, startedAtMs) {
  const parsed = Date.parse(expiresAt ?? '');
  return Number.isFinite(parsed) ? parsed : startedAtMs + POLL_FALLBACK_TIMEOUT_MS;
}
